import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { memberService } from '../../services/member.service';
import { serviceRequestService } from '../../services/service-request.service';
import { Loading } from '../../components/ui/Loading';

export const MemberDashboardPage = () => {
  const [profile, setProfile] = useState<any>(null);
  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    completedRequests: 0
  });
  const [loading, setLoading] = useState(true);

  const quickActions = [
    { type: 'HOSPITAL_APPOINTMENT', label: 'Book Hospital Appointment', icon: '🏥', color: 'bg-blue-500' },
    { type: 'DIAGNOSTIC_TEST', label: 'Book Diagnostic Test', icon: '🔬', color: 'bg-green-500' },
    { type: 'INSURANCE_SUPPORT', label: 'Request Insurance Support', icon: '📋', color: 'bg-purple-500' },
    { type: 'AMBULANCE', label: 'Request Ambulance', icon: '🚑', color: 'bg-red-500' },
    { type: 'HOME_CARE', label: 'Request Home Care', icon: '🏠', color: 'bg-yellow-500' },
    { type: 'COORDINATOR_CONTACT', label: 'Contact Service Coordinator', icon: '📞', color: 'bg-indigo-500' }
  ];

  useEffect(() => {
    loadDashboardData();

    const interval = setInterval(() => {
      loadDashboardData(true);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      // Fetch profile and requests in parallel
      const [profileData, allRequests, pendingRequests, completedRequests] = await Promise.all([
        memberService.getMemberProfile(),
        serviceRequestService.getMemberRequests({ limit: 1 }),
        serviceRequestService.getMemberRequests({ status: 'REQUESTED', limit: 1 }),
        serviceRequestService.getMemberRequests({ status: 'COMPLETED', limit: 1 })
      ]);

      setProfile(profileData);
      setStats({
        totalRequests: allRequests.total || 0,
        pendingRequests: pendingRequests.total || 0,
        completedRequests: completedRequests.total || 0
      });
    } catch (error: any) {
      console.error('Failed to load dashboard data:', error);
      // Only show toast on initial load failure to avoid spamming
      if (!silent) toast.error('Failed to load dashboard data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  if (loading) return <Loading />;

  if (loading) return <Loading />;

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Hello, {profile?.name?.split(' ')[0] || 'Member'} 👋
          </h1>
          <p className="text-slate-500 mt-1">Here is your membership overview</p>
        </div>
        <div className="hidden md:block">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
            <span className="w-2 h-2 mr-2 bg-emerald-500 rounded-full animate-pulse"></span>
            System Operational
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Membership Card & Stats */}
        <div className="lg:col-span-2 space-y-8">
          {/* Membership Card - Digital Wallet Style */}
          {profile && (
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-700 text-white shadow-xl shadow-indigo-200 transition-transform hover:scale-[1.01] duration-300">
              {/* Decorative Circles */}
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-white opacity-10 blur-2xl"></div>
              <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 rounded-full bg-white opacity-10 blur-2xl"></div>

              <div className="relative p-8 flex flex-col justify-between h-full min-h-[220px]">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-indigo-200 text-sm font-medium tracking-wider uppercase">Membership ID</p>
                    <p className="text-2xl font-mono mt-1 tracking-widest">{profile.membershipId}</p>
                  </div>
                  <div className="w-12 h-8 rounded bg-white/20 backdrop-blur-sm border border-white/10 flex items-center justify-center">
                    <div className="w-8 h-5 border border-white/50 rounded-sm flex items-center justify-center">
                      <div className="w-6 h-3 border-t border-b border-white/50"></div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-between items-end">
                  <div>
                    <p className="text-indigo-200 text-xs uppercase tracking-wider mb-1">Valid Until</p>
                    <p className="text-lg font-medium">{new Date(profile.validUntil).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white/20 backdrop-blur-md border border-white/10 ${profile.membershipStatus === 'ACTIVE' ? 'text-emerald-300 border-emerald-400/30' : 'text-amber-300'}`}>
                      {profile.membershipStatus}
                    </span>
                    <p className="text-sm mt-1 text-indigo-100">{profile.membershipType}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center hover:border-indigo-100 transition-colors">
              <span className="text-3xl font-bold text-slate-800">{stats.totalRequests}</span>
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider mt-1">Total Requests</span>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center hover:border-amber-100 transition-colors">
              <span className="text-3xl font-bold text-amber-500">{stats.pendingRequests}</span>
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider mt-1">Pending</span>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center hover:border-emerald-100 transition-colors">
              <span className="text-3xl font-bold text-emerald-500">{stats.completedRequests}</span>
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider mt-1">Completed</span>
            </div>
          </div>

          {/* Info Cards Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/member/benefits" className="group bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md hover:border-indigo-100 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-2xl group-hover:bg-purple-600 group-hover:text-white transition-colors">
                  🎁
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">My Benefits</h3>
                  <p className="text-sm text-slate-500">Explore your perks</p>
                </div>
              </div>
            </Link>
            <Link to="/member/support" className="group bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md hover:border-indigo-100 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  💬
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Support Center</h3>
                  <p className="text-sm text-slate-500">Get help 24/7</p>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Right Column: Quick Actions */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <span className="w-1.5 h-6 bg-indigo-500 rounded-full"></span>
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 gap-3">
            {quickActions.map((action) => (
              <Link
                key={action.type}
                to={`/member/requests/new?type=${action.type}`}
                className="group relative overflow-hidden bg-white p-4 rounded-xl shadow-sm border border-slate-100 hover:shadow-md hover:border-indigo-100 transition-all duration-300 flex items-center gap-4"
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl text-white shadow-sm transition-transform group-hover:scale-110 duration-300 ${action.color.replace('bg-', 'bg-gradient-to-br from-').replace('500', '500 to-') + action.color.replace('bg-', '').replace('500', '600')}`}>
                  {action.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800 group-hover:text-indigo-600 transition-colors">{action.label}</h3>
                  <p className="text-xs text-slate-400">Tap to start request</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
